import type { CalibrationDataset, IntensitySeries, MonteCarloResult, RayPath } from '../types'
import { formatNumber } from './ui'

const lineColors = [
  'oklch(0.53 0.15 232)',
  'oklch(0.55 0.14 150)',
  'oklch(0.60 0.16 55)',
  'oklch(0.52 0.16 310)',
  'oklch(0.50 0.15 20)',
  'oklch(0.47 0.11 270)',
  'oklch(0.58 0.12 195)',
  'oklch(0.48 0.09 95)',
]

const dashPatterns = ['', '8 3', '3 3', '10 3 2 3', '2 2', '12 4', '6 2 2 2', '1 3']

function pathFromSeries(
  x: number[],
  y: number[],
  xMin: number,
  xMax: number,
  yMax: number,
  width: number,
  height: number,
): string {
  return x
    .map((value, index) => {
      const px = 56 + ((value - xMin) / Math.max(xMax - xMin, 1e-12)) * (width - 76)
      const py = 18 + (1 - y[index] / Math.max(yMax, 1e-15)) * (height - 58)
      return `${index === 0 ? 'M' : 'L'}${px.toFixed(2)},${py.toFixed(2)}`
    })
    .join(' ')
}

export function IntensityPlot({
  series,
  targets = [],
}: {
  series: IntensitySeries[]
  targets?: number[]
}) {
  const width = 840
  const height = 300
  const allX = series.flatMap((item) => item.xMm)
  const xMin = Math.min(...allX)
  const xMax = Math.max(...allX)
  const yMax = Math.max(...series.flatMap((item) => item.intensity), 1e-15)
  return (
    <figure className="plot-frame">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-labelledby="intensity-title intensity-desc">
        <title id="intensity-title">Relative intensity by source view</title>
        <desc id="intensity-desc">
          Weighted ray energy at the viewing plane. Curves share one launched-energy scale.
        </desc>
        <rect x="56" y="18" width={width - 76} height={height - 58} className="plot-area" />
        {[0, 0.25, 0.5, 0.75, 1].map((fraction) => {
          const y = 18 + (1 - fraction) * (height - 58)
          return (
            <g key={fraction}>
              <line x1="56" x2={width - 20} y1={y} y2={y} className="grid-line" />
              <text x="50" y={y + 4} textAnchor="end" className="axis-label">
                {formatNumber(yMax * fraction, 2)}
              </text>
            </g>
          )
        })}
        {targets.map((target, index) => {
          const x = 56 + ((target - xMin) / Math.max(xMax - xMin, 1e-12)) * (width - 76)
          return (
            <g key={`${target}-${index}`}>
              <line x1={x} x2={x} y1="18" y2={height - 40} className="target-line" />
              <text x={x + 3} y="31" className="target-label">
                T{index + 1}
              </text>
            </g>
          )
        })}
        {series.map((item, index) => (
          <path
            key={item.view}
            d={pathFromSeries(item.xMm, item.intensity, xMin, xMax, yMax, width, height)}
            fill="none"
            stroke={lineColors[index % lineColors.length]}
            strokeDasharray={dashPatterns[index % dashPatterns.length]}
            strokeWidth="1.8"
            vectorEffect="non-scaling-stroke"
          />
        ))}
        <line x1="56" x2={width - 20} y1={height - 40} y2={height - 40} className="axis-line" />
        <text x="56" y={height - 20} className="axis-label">
          {formatNumber(xMin, 1)}
        </text>
        <text x={width - 20} y={height - 20} textAnchor="end" className="axis-label">
          {formatNumber(xMax, 1)}
        </text>
        <text x={width / 2} y={height - 7} textAnchor="middle" className="axis-title">
          Viewer position x (mm)
        </text>
      </svg>
      <figcaption>
        <span>Relative accepted ray weight per viewer bin</span>
        <span className="plot-legend">
          {series.map((item, index) => (
            <span key={item.view} style={{ color: lineColors[index % lineColors.length] }}>
              <i style={{ borderTopStyle: index % 3 === 0 ? 'solid' : 'dashed' }} /> View {item.view}
            </span>
          ))}
        </span>
      </figcaption>
    </figure>
  )
}

export function RayPlot({ rays }: { rays: RayPath[] }) {
  const transmitted = rays.filter((ray) => ray.points.length >= 2)
  const points = transmitted.flatMap((ray) => ray.points)
  if (!points.length) return null
  const width = 840
  const height = 320
  const xValues = points.map((point) => point.x)
  const yValues = points.map((point) => point.y)
  const xMin = Math.min(...xValues)
  const xMax = Math.max(...xValues)
  const yMin = Math.min(...yValues)
  const yMax = Math.max(...yValues)
  const scaleX = (x: number) => 60 + ((x - xMin) / Math.max(xMax - xMin, 1e-12)) * (width - 82)
  const scaleY = (y: number) => 18 + ((yMax - y) / Math.max(yMax - yMin, 1e-12)) * (height - 62)
  return (
    <figure className="plot-frame ray-plot">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-labelledby="ray-title ray-desc">
        <title id="ray-title">Ray paths from the active simulation</title>
        <desc id="ray-desc">
          These paths are sampled directly from the same records used to build the intensity distributions.
        </desc>
        <rect x="60" y="18" width={width - 82} height={height - 62} className="plot-area" />
        {transmitted.slice(0, 140).map((ray, index) => (
          <polyline
            key={index}
            points={ray.points.map((point) => `${scaleX(point.x)},${scaleY(point.y)}`).join(' ')}
            fill="none"
            stroke={
              ray.outcome === 'transmitted'
                ? lineColors[ray.view % lineColors.length]
                : 'oklch(0.55 0.03 240)'
            }
            strokeDasharray={ray.outcome === 'transmitted' ? undefined : '3 3'}
            strokeOpacity={ray.outcome === 'transmitted' ? 0.32 : 0.18}
            strokeWidth="1"
            vectorEffect="non-scaling-stroke"
          />
        ))}
        {[yMin, 0, yMax]
          .filter((value, index, array) => value >= yMin && value <= yMax && array.indexOf(value) === index)
          .map((value) => (
            <g key={value}>
              <line x1="60" x2={width - 22} y1={scaleY(value)} y2={scaleY(value)} className="station-line" />
              <text x="54" y={scaleY(value) + 4} textAnchor="end" className="axis-label">
                {value === 0 ? 'lens' : `${formatNumber(value, 1)} mm`}
              </text>
            </g>
          ))}
        <text x={width / 2} y={height - 7} textAnchor="middle" className="axis-title">
          Horizontal position x, propagation shown vertically
        </text>
      </svg>
      <figcaption>
        <span>Sampled paths, horizontal and axial scales differ</span>
        <span>Dashed gray paths were rejected</span>
      </figcaption>
    </figure>
  )
}

export function DistributionPlot({
  values,
  label,
  unit,
}: {
  values: number[]
  label: string
  unit: string
}) {
  const finite = values.filter(Number.isFinite)
  if (!finite.length) return <div className="mini-empty">No finite samples</div>
  const min = Math.min(...finite)
  const max = Math.max(...finite)
  const count = Math.min(28, Math.max(8, Math.round(Math.sqrt(finite.length))))
  const bins = Array(count).fill(0)
  finite.forEach((value) => {
    const index = max === min ? 0 : Math.min(count - 1, Math.floor(((value - min) / (max - min)) * count))
    bins[index] += 1
  })
  const peak = Math.max(...bins)
  return (
    <figure className="distribution-plot">
      <figcaption>{label}</figcaption>
      <div className="bars" aria-label={`${label} histogram`}>
        {bins.map((value, index) => (
          <span key={index} style={{ height: `${Math.max(3, (value / peak) * 100)}%` }} />
        ))}
      </div>
      <div className="range-labels">
        <span>{formatNumber(min, 3)} {unit}</span>
        <span>{formatNumber(max, 3)} {unit}</span>
      </div>
    </figure>
  )
}

export function TolerancePlots({ result }: { result: MonteCarloResult }) {
  return (
    <div className="distribution-grid">
      <DistributionPlot values={result.distributions.crosstalk} label="Crosstalk" unit="fraction" />
      <DistributionPlot
        values={result.distributions.zoneCenterErrorMm}
        label="Zone-center error"
        unit="mm"
      />
      <DistributionPlot
        values={result.distributions.predictedViewingDistanceMm}
        label="Predicted viewing distance"
        unit="mm"
      />
      <DistributionPlot
        values={result.distributions.driftSourceCells}
        label="Accumulated drift"
        unit="source cells"
      />
    </div>
  )
}

export function MeasurementPlot({
  dataset,
  simulated,
}: {
  dataset: CalibrationDataset
  simulated?: IntensitySeries[] | null
}) {
  const width = 840
  const height = 300
  const xMin = dataset.xMm[0]
  const xMax = dataset.xMm[dataset.xMm.length - 1]
  const measuredMax = Math.max(...dataset.intensities.flat(), 1e-15)
  const simulatedMax = simulated ? Math.max(...simulated.flatMap((item) => item.intensity), 1e-15) : 1
  const scaleX = (x: number) => 56 + ((x - xMin) / Math.max(xMax - xMin, 1e-12)) * (width - 76)
  const scaleY = (y: number) => 18 + (1 - y) * (height - 58)
  return (
    <figure className="plot-frame">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-labelledby="measurement-title">
        <title id="measurement-title">Measured intensity with optional simulated overlay</title>
        <rect x="56" y="18" width={width - 76} height={height - 58} className="plot-area" />
        {dataset.intensities.map((values, view) => (
          <path
            key={`measured-${view}`}
            d={dataset.xMm
              .map(
                (x, index) =>
                  `${index ? 'L' : 'M'}${scaleX(x).toFixed(2)},${scaleY(values[index] / measuredMax).toFixed(2)}`,
              )
              .join(' ')}
            fill="none"
            stroke={lineColors[view % lineColors.length]}
            strokeWidth="2"
          />
        ))}
        {simulated?.map((item, view) => {
          const points = item.xMm
            .map((x, index) => ({ x, y: item.intensity[index] / simulatedMax }))
            .filter((point) => point.x >= xMin && point.x <= xMax)
          return (
            <path
              key={`sim-${view}`}
              d={points
                .map(
                  (point, index) =>
                    `${index ? 'L' : 'M'}${scaleX(point.x).toFixed(2)},${scaleY(point.y).toFixed(2)}`,
                )
                .join(' ')}
              fill="none"
              stroke={lineColors[view % lineColors.length]}
              strokeDasharray="4 3"
              strokeOpacity="0.65"
              strokeWidth="1.5"
            />
          )
        })}
        <line x1="56" x2={width - 20} y1={height - 40} y2={height - 40} className="axis-line" />
        <text x="56" y={height - 20} className="axis-label">{formatNumber(xMin, 1)}</text>
        <text x={width - 20} y={height - 20} textAnchor="end" className="axis-label">
          {formatNumber(xMax, 1)}
        </text>
        <text x={width / 2} y={height - 7} textAnchor="middle" className="axis-title">
          Measurement position x (mm)
        </text>
      </svg>
      <figcaption>
        <span>Solid: measured, globally normalized</span>
        {simulated ? <span>Dashed: current simulation, separately normalized for shape comparison</span> : null}
      </figcaption>
    </figure>
  )
}
